import React from 'react';
import ActivityCalendar from './ActivityCalendar';
import { ActionButtonLayout } from '../ActionButtonLayout';
import MainContentContainer from '../MainContentContainer';
import DesktopRecentActivitySection from './DesktopRecentActivitySection';
import { useIsMobileLayout } from '../../../hooks/useIsMobileLayout';
import MobileActivitySection from './MobileActivitySection';
import PendingTransferRequestAlert from './PendingTransferRequestAlert';
import { getIntl } from '../../../utils/intl';
import { useProfilesTabNavigation } from '../../../utils/appNavigationNames';
import { useHasPermission } from '../Permissions/hooks';
import { categoryToDomain } from '../../../utils/resourceUtils/medicalDataPermissions';

const intl = getIntl('profileActivityScreen', {
    addVisit: 'Add Visit',
    sendMessage: 'Message',
    addNote: 'Add Note',
});

export type ProfileActivityScreenV2OwnProps = {
    profileId: string;
};

export type ProfileActivityScreenV2Props = ProfileActivityScreenV2OwnProps;

const ProfileActivityScreenV2: React.FC<ProfileActivityScreenV2Props> = (props) => {
    const isMobile = useIsMobileLayout();
    const { navigate } = useProfilesTabNavigation();
    // Creating a visit needs edit on visits. Offering it without that permission sends the user through
    // the whole add-visit form only for the save to be refused, with nothing but a generic error to show
    // why.
    const canEditVisits = useHasPermission(props.profileId, categoryToDomain('visit'), 'edit');

    return (
        <ActionButtonLayout
            actions={[
                {
                    icon: 'Send',
                    title: intl('sendMessage'),
                    onPress: () =>
                        navigate('message-profile', {
                            profileId: props.profileId,
                        }),
                    testID: 'send-message-action',
                    show: isMobile,
                },
                {
                    icon: 'Plus',
                    title: intl('addNote'),
                    onPress: () =>
                        navigate('add-note', {
                            medicalProfileId: props.profileId,
                            entityType: 'medical-profile',
                        }),
                    testID: 'add-note-action',
                    show: isMobile,
                },
                {
                    icon: 'Plus',
                    title: intl('addVisit'),
                    onPress: () =>
                        navigate('add-data', {
                            profileId: props.profileId,
                            category: 'visit',
                            sourceResourceId: null,
                        }),
                    testID: 'add-visit-action',
                    show: isMobile && canEditVisits,
                },
            ]}
            restrictedWidthVariant="lg"
            scrollable
        >
            <MainContentContainer style={{ alignSelf: 'stretch', flex: 1, alignItems: 'center' }}>
                <PendingTransferRequestAlert profileId={props.profileId} />
                <ActivityCalendar medicalProfileId={props.profileId} />
                {isMobile ? (
                    <MobileActivitySection profileId={props.profileId} />
                ) : (
                    <DesktopRecentActivitySection profileId={props.profileId} />
                )}
            </MainContentContainer>
        </ActionButtonLayout>
    );
};

export default React.memo(ProfileActivityScreenV2);
